import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  input,
  model,
  signal,
  TemplateRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { WooColumnParent } from './woo-table-column/column-parrent.component';
import { WooTableColumn } from './woo-table-column/woo-table-column.component';
import { GroupSegment, TableRow } from './woo-table.type';

@Component({
  selector: 'app-woo-table',
  imports: [CommonModule, FormsModule],
  templateUrl: './woo-table.component.html',
  styleUrls: ['./woo-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WooTableComponent {
  readonly columnDefs = contentChildren(WooTableColumn, { descendants: true });
  readonly groupDefs = contentChildren(WooColumnParent, { descendants: true });
  public expandedTpl = input<TemplateRef<any> | null>(null);
  public loaderTpl = input<TemplateRef<any> | null>(null);

  public data = model<TableRow[]>([]);
  public noDataText = input<string>(''); //a text to display in no data rows
  public expandable = input<boolean>(false); //enable expandable rows
  public accordion = input<boolean>(false); //enable accordion
  public emptyCellText = input<string>(''); //a text to display in empty cells
  public sumTitle = input<string | null>(null); //Add a title to the sum row, it also adds the sum row. It will sum the values of the columns.
  public fixedHeader = input<boolean>(false); //enable a fixed header
  public fixedLayout = input<boolean>(false); //use a fixed table layout so column widths stay stable regardless of cell content (e.g. expanded rows)
  public maxHeight = input<string | null>(null); //enable a max height for the table
  public debug = input<boolean>(false); //enable debug mode

  public sortColumn = signal<string | null>(null);
  public sortDirection = signal<'asc' | 'desc'>('asc');

  readonly groupSegments = computed<GroupSegment[]>(() => {
    const cols = this.columnDefs();
    const parents = this.groupDefs();

    const map = new Map<string, WooColumnParent>();
    for (const p of parents) {
      for (const name of p.children() ?? []) {
        if (!map.has(name)) map.set(name, p);
      }
    }

    const segs: GroupSegment[] = [];
    let current: WooColumnParent | null = null;
    let count = 0;

    const push = () => {
      if (!count) return;
      const label = current ? current.title() : '';
      const tpl = current ? current.template() : null;
      segs.push({
        label,
        colspan: count,
        tpl,
        key: (label || 'ungrouped') + '-' + segs.length,
        isGroup: !!label,
      });
    };

    for (const col of cols) {
      const p = map.get(col.name()) ?? null;
      if (p === current) {
        count++;
      } else {
        push();
        current = p;
        count = 1;
      }
    }
    push();

    return segs;
  });

  toggleSort(column: string): void {
    if (this.sortColumn() === column) {
      this.sortDirection.update((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }
  }

  public hasGroupRow = computed(() => this.groupSegments().some((s) => s.isGroup));

  public columnSums = computed(() => {
    const sums: Record<string, number> = {};
    const rows = this.data();

    for (const col of this.columnDefs()) {
      const key = col.name();
      const numericValues = rows.map((row) => Number(row?.[key])).filter((val) => !isNaN(val));

      if (numericValues.length > 0) {
        sums[key] = numericValues.reduce((a, b) => a + b, 0);
      }
    }

    return sums;
  });

  public setCell = (row: TableRow, key: string, value: unknown): void => {
    this.data.update((arr) => arr.map((r) => (r === row ? { ...r, [key]: this.coerce(value) } : r)));
  };

  public coerce(v: unknown): unknown {
    if (v === '' || v == null) return v;
    const n = Number(v as any);
    return Number.isFinite(n) ? n : v;
  }

  public isNumeric(value: unknown): value is number | string {
    return value !== '' && value != null && !Number.isNaN(Number(value));
  }

  public sortedData = computed(() => {
    const data = [...(this.data() ?? [])];
    const sortCol = this.sortColumn();
    const dir = this.sortDirection();

    if (!sortCol) return data;

    const sign = dir === 'asc' ? 1 : -1;

    return data.sort((a, b) => {
      const av = a[sortCol as keyof TableRow] as unknown;
      const bv = b[sortCol as keyof TableRow] as unknown;

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (typeof av === 'number' && typeof bv === 'number') {
        return sign * (av - bv);
      }

      return sign * String(av).localeCompare(String(bv));
    });
  });

  public openIndexes = signal<Set<number>>(new Set());

  public isExpandedAt = (i: number): boolean => this.openIndexes().has(i);

  public toggleExpandedAt = (i: number): void => {
    if (!this.expandable()) return;
    const next = new Set(this.openIndexes());
    next.has(i) ? next.delete(i) : next.add(i);
    this.openIndexes.set(next);
  };
}
